/*
 * Size Up Your Pup - sizing quiz modal
 *
 * Reuses the existing quiz section (#shopify-section-quiz-section) rather than
 * duplicating it: the node is moved into the modal on open and returned to its
 * original position on close. Because the node itself is moved, every handler
 * bound in assets/quiz.js survives untouched.
 *
 * All listeners are delegated from `document` and bound once, so opening and
 * closing repeatedly can never stack duplicates.
 */
(function () {
  'use strict';

  var OPEN_CLASS = 'size-quiz-modal--open';
  var BODY_CLASS = 'size-quiz-modal-open';
  var QUIZ_ID = 'shopify-section-quiz-section';
  // Slightly longer than the 0.55s slide-up in size-quiz-modal.css, so the
  // dialog is never hidden mid-transition.
  var TRANSITION_MS = 620;

  var modal = null;
  var mount = null;
  var quiz = null;
  var homeMarker = null;
  var lastFocused = null;
  var restoreTimer = null;

  function init() {
    modal = document.getElementById('SizeQuizModal');
    if (!modal) return;
    mount = modal.querySelector('[data-size-quiz-mount]');

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
  }

  function isOpen() {
    return !!modal && modal.classList.contains(OPEN_CLASS);
  }

  function moveQuizIn() {
    quiz = document.getElementById(QUIZ_ID);
    if (!quiz || !mount || quiz.parentNode === mount) return;

    homeMarker = document.createComment('size-quiz-home');
    quiz.parentNode.insertBefore(homeMarker, quiz);
    mount.appendChild(quiz);
    // The section is collapsed to height:0 until `.reveal` is applied.
    quiz.classList.add('reveal');
  }

  function moveQuizHome() {
    if (!quiz || !homeMarker || !homeMarker.parentNode) return;
    quiz.classList.remove('reveal');
    homeMarker.parentNode.insertBefore(quiz, homeMarker);
    homeMarker.parentNode.removeChild(homeMarker);
    homeMarker = null;
    quiz = null;
  }

  function open(trigger) {
    if (!modal || isOpen()) return;

    if (restoreTimer) {
      clearTimeout(restoreTimer);
      restoreTimer = null;
    }

    lastFocused = trigger || document.activeElement;
    moveQuizIn();

    modal.hidden = false;
    // Force a reflow so the slide-down transition actually runs.
    void modal.offsetWidth;
    modal.classList.add(OPEN_CLASS);
    document.body.classList.add(BODY_CLASS);

    var closeBtn = modal.querySelector('.size-quiz-modal__close');
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    if (!modal || !isOpen()) return;

    modal.classList.remove(OPEN_CLASS);
    document.body.classList.remove(BODY_CLASS);

    restoreTimer = setTimeout(function () {
      restoreTimer = null;
      if (isOpen()) return;
      moveQuizHome();
      modal.hidden = true;
    }, TRANSITION_MS);

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  function onClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var opener = target.closest('[data-size-quiz-open]');
    if (opener) {
      event.preventDefault();
      open(opener);
      return;
    }

    if (!isOpen()) return;

    // Modal chrome (overlay + mobile header close) and the quiz's own
    // in-card close links both dismiss the modal.
    if (target.closest('[data-size-quiz-close]') || target.closest('.close-quiz')) {
      event.preventDefault();
      close();
    }
  }

  function onKeydown(event) {
    if (!isOpen()) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === 'Tab') trapFocus(event);
  }

  function trapFocus(event) {
    var focusable = modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    var visible = [];
    for (var i = 0; i < focusable.length; i++) {
      var el = focusable[i];
      if (el.offsetParent !== null && getComputedStyle(el).visibility !== 'hidden') {
        visible.push(el);
      }
    }
    if (!visible.length) return;

    var first = visible[0];
    var last = visible[visible.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // This script is deferred, so the document is already parsed and the modal
  // markup is present — init straight away so the body flag is set before any
  // click handler can read it.
  if (document.body) {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
